import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Inbox, PencilLine } from 'lucide-react'
import {
    CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from '@/components/ui/command'
import type { Form, Submission } from '../types'
import { ICONS } from './icons'
import { categoryLabel, cleanDescription, groupByCategory, iconFor } from './model'

interface Props {
    open: boolean
    onOpenChange: (o: boolean) => void
    forms: Form[] | null
    drafts: Submission[] | null
    waitingCount: number
    onStart: (f: Form) => void
}

/** Global ⌘K / Ctrl-K binding for the page. */
export function useQuickStartShortcut(toggle: () => void) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
                e.preventDefault()
                toggle()
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [toggle])
}

export function QuickStartPalette({ open, onOpenChange, forms, drafts, waitingCount, onStart }: Props) {
    const navigate = useNavigate()
    const go = (fn: () => void) => { onOpenChange(false); fn() }

    return (
        <CommandDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Quick start"
            description="Search for a form to start, or a draft to continue."
        >
            <CommandInput placeholder="Start a request…" aria-label="Search forms and drafts" />
            <CommandList>
                <CommandEmpty>No form matches that.</CommandEmpty>
                {(drafts?.length ?? 0) > 0 && (
                    <CommandGroup heading="Continue a draft">
                        {drafts!.slice(0, 5).map((s) => (
                            <CommandItem
                                key={s.id}
                                value={`draft ${s.title} ${s.form_name} ${s.number}`}
                                onSelect={() => go(() => void navigate({ to: '/submissions/$id/edit', params: { id: s.id } }))}
                            >
                                <PencilLine />
                                <span className="truncate">{s.title && s.title !== s.form_name ? s.title : s.form_name}</span>
                                <CommandShortcut>Draft</CommandShortcut>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}
                {waitingCount > 0 && (
                    <CommandGroup heading="Approvals">
                        <CommandItem value="approvals inbox waiting review" onSelect={() => go(() => void navigate({ to: '/inbox' }))}>
                            <Inbox />
                            <span>Review {waitingCount} waiting on you</span>
                        </CommandItem>
                    </CommandGroup>
                )}
                {((drafts?.length ?? 0) > 0 || waitingCount > 0) && <CommandSeparator />}
                {groupByCategory(forms ?? []).map((g) => (
                    <CommandGroup key={g.category} heading={`Start · ${g.label}`}>
                        {g.forms.map((f) => {
                            const Icon = ICONS[iconFor(f)]
                            return (
                                <CommandItem
                                    key={f.id}
                                    value={`${f.name} ${categoryLabel(f.category)} ${cleanDescription(f)} ${f.slug}`}
                                    onSelect={() => go(() => onStart(f))}
                                >
                                    <Icon />
                                    <span className="truncate">{f.name}</span>
                                </CommandItem>
                            )
                        })}
                    </CommandGroup>
                ))}
            </CommandList>
        </CommandDialog>
    )
}
